import { actionPath, hostFetch } from '@bridge/app-sdk'
import type { UiContract } from '@bridge/app-sdk/ui'
import {
  AppBridge,
  PostMessageTransport,
} from '@modelcontextprotocol/ext-apps/app-bridge'
import { readDelay, readResponse } from '@web/api/read'
import type { AppDefinition } from '@web/api/types'
import { OpeningState } from '@web/components/OpeningState'
import { SessionWorkspaceSkeleton } from '@web/components/SessionSkeleton'
import { publishAppContext } from '@web/lib/app-context-sync'
import { appConfiguration } from '@web/state/app-configuration'
import {
  BUILDER_WORKFLOW_EVENT,
  publishBuilderWorkflow,
} from '@web/state/builder-workflow'
import { getSessionStore } from '@web/state/session-store'
import {
  recalledWorkspace,
  rememberWorkspace,
} from '@web/state/workspace-state'
import { useEffect, useRef, useState } from 'react'

/** Load portable app documents through the host's separate-origin MCP sandbox. */
export function AppPane({
  app,
  bindingId,
  sessionId,
  sessionStarting = false,
  startupShareSlug,
}: {
  app: AppDefinition
  bindingId: string
  sessionId: string | null
  sessionStarting?: boolean
  startupShareSlug?: string
}) {
  const iframe = useRef<HTMLIFrameElement>(null)
  const [error, setError] = useState('')
  const [stateError, setStateError] = useState('')
  const [connectionError, setConnectionError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const refreshRef = useRef<() => void>(() => {})
  const [ready, setReady] = useState(false)
  const [backendPaused, setBackendPaused] = useState(false)
  const [hasState, setHasState] = useState(false)
  const configuration = appConfiguration(app)
  const backendStarting =
    configuration.backend.mode === 'per_session' && sessionStarting
  const startingRef = useRef(backendStarting)
  startingRef.current = backendStarting
  const entrypoint = configuration.frontend.pane?.entrypoint
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly restarts a failed view.
  useEffect(() => {
    let disposed = false
    let themeObserver: MutationObserver | undefined
    let bridge: AppBridge | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    let refreshTimer: ReturnType<typeof setInterval> | undefined
    let initialized = false
    let refreshing = false
    let refreshAgain = false
    let builderRevision = -1
    const cacheKey = `${bindingId}:${app.digest ?? app.version}`
    let lastState = ''
    let lease = ''
    const uiInvocations = new Map<string, string>()
    let uiContract: UiContract | undefined
    let uiTools: string[] = []
    let boundSessionId = sessionId
    let contextUpdate: Promise<unknown> = Promise.resolve()
    const pendingOnePagerResults: Record<string, unknown>[] = []
    const publishComposerContext = (
      value: Record<string, unknown> | null,
      pending?: Promise<unknown>,
      retry?: () => Promise<unknown>,
    ) => {
      if (!boundSessionId) return
      publishAppContext(
        boundSessionId,
        bindingId,
        viewId,
        value,
        pending,
        retry,
      )
    }
    let pendingInspection: {
      type: 'chronicle.inspect'
      node_id: string
      artifact_id: string
    } | null = null
    const controller = new AbortController()
    const viewId = crypto.randomUUID()
    const root = `/v1/apps/bindings/${encodeURIComponent(bindingId)}`
    const viewPath = `${root}/views/${viewId}`
    const sendInspection = async () => {
      if (!initialized || disposed || !pendingInspection) return
      const inspection = pendingInspection
      pendingInspection = null
      await bridge?.sendToolResult({
        content: [],
        structuredContent: { data: inspection },
      })
    }
    const inspect = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          bindingId?: string
          nodeId?: string
          artifactId?: string
        }>
      ).detail
      if (
        app.id !== 'chronicle' ||
        detail?.bindingId !== bindingId ||
        typeof detail.nodeId !== 'string' ||
        typeof detail.artifactId !== 'string'
      )
        return
      if (
        !/^[A-Za-z0-9_:-]{1,180}$/.test(detail.nodeId) ||
        !/^artifact-[a-f0-9]{20}$/.test(detail.artifactId)
      )
        return
      pendingInspection = {
        type: 'chronicle.inspect',
        node_id: detail.nodeId,
        artifact_id: detail.artifactId,
      }
      void sendInspection().catch(() => {
        if (!disposed)
          setStateError(
            'The analysis selection could not be opened. Try again.',
          )
      })
    }
    window.addEventListener('bridge:analysis-inspect', inspect)
    const revokeAccess = async () => {
      if (disposed) return
      try {
        await bridge?.sendToolResult({
          content: [],
          structuredContent: { data: { revoked: true } },
        })
      } catch {
        /* The guest may already have disconnected. */
      }
      disposed = true
      themeObserver?.disconnect()
      controller.abort()
      clearTimeout(timer)
      clearInterval(refreshTimer)
      void bridge?.close()
      if (iframe.current) iframe.current.src = 'about:blank'
      publishComposerContext(null)
      setReady(false)
      setError('Your access to this application has changed.')
      window.dispatchEvent(new Event('bridge:identity-expired'))
    }
    const refreshState = async () => {
      if (!initialized || disposed || startingRef.current) return
      if (refreshing) {
        refreshAgain = true
        return
      }
      refreshing = true
      try {
        const response = await readResponse(`${root}/state`, {
          credentials: 'include',
          signal: controller.signal,
        })
        if ([401, 403, 404].includes(response.status)) {
          await revokeAccess()
          return
        }
        // Per-session state pauses during sleep; shared state must remain available.
        if (
          configuration.backend.mode === 'per_session' &&
          (response.status === 409 || response.status === 423)
        ) {
          if (!disposed) {
            setBackendPaused(true)
            setStateError('')
          }
          return
        }
        if (!disposed) setBackendPaused(false)
        if (
          configuration.backend.mode === 'shared' &&
          response.status === 409
        ) {
          throw new Error(
            boundSessionId
              ? 'The shared app has changed. Resume this session to load the latest app version. If it is awake, put it to sleep first.'
              : 'The shared app has changed. Open a new session to load the latest app version.',
          )
        }
        if (!response.ok)
          throw new Error('The saved app state could not be refreshed.')
        const data = await response.json()
        if (app.id === 'agent-builder' && data.workflow) {
          builderRevision = Number(data.workflow.revision)
          publishBuilderWorkflow(bindingId, data.workflow)
        }
        const serialized = JSON.stringify(data)
        if (!disposed && serialized !== lastState) {
          await bridge?.sendToolResult({
            content: [],
            structuredContent: { data },
          })
          lastState = serialized
          if (!disposed) {
            rememberWorkspace(cacheKey, data)
            setHasState(true)
          }
        }
        if (!disposed) setStateError('')
      } catch (failure) {
        if (!disposed && !startingRef.current)
          setStateError((failure as Error).message)
      } finally {
        refreshing = false
        if (refreshAgain && !disposed) {
          refreshAgain = false
          void refreshState()
        }
      }
    }
    refreshRef.current = () => {
      void refreshState()
    }
    const receiveBuilderWorkflow = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          bindingId?: string
          workflow?: { revision?: number }
        }>
      ).detail
      const revision = detail?.workflow?.revision
      if (
        app.id === 'agent-builder' &&
        detail?.bindingId === bindingId &&
        typeof revision === 'number' &&
        Number.isInteger(revision) &&
        revision > builderRevision
      )
        void refreshState()
    }
    window.addEventListener(BUILDER_WORKFLOW_EVENT, receiveBuilderWorkflow)
    setError('')
    setStateError('')
    setConnectionError('')
    setReady(false)
    setBackendPaused(false)
    setHasState(false)
    void (async () => {
      try {
        if (!entrypoint)
          throw new Error('This app has no configured workspace document.')
        const [binding, response, catalog] = await Promise.all([
          readResponse(root, {
            credentials: 'include',
            signal: controller.signal,
          }).then(async (response) => {
            if (!response.ok)
              throw new Error('This workspace is no longer available.')
            return response.json()
          }),
          readResponse(
            `${root}/assets/${entrypoint.split('/').map(encodeURIComponent).join('/')}${app.digest ? `?packageDigest=${encodeURIComponent(app.digest)}` : ''}`,
            { credentials: 'include', signal: controller.signal },
          ),
          hostFetch(
            `/v1/apps/actions?bindingId=${encodeURIComponent(bindingId)}`,
          ),
        ])
        boundSessionId =
          typeof binding.session === 'string' && binding.session
            ? binding.session
            : sessionId
        if (!response.ok)
          throw new Error('The app workspace document is unavailable.')
        const html = await response.text()
        const sandbox = app.sandboxUrl ?? binding.app?.sandboxUrl
        if (!sandbox || new URL(sandbox).origin === location.origin)
          throw new Error(
            'A separate app sandbox origin is required to open this workspace.',
          )
        if (!iframe.current?.contentWindow || disposed) return
        const frame = iframe.current
        const canChat = configuration.ui.mode !== 'ui' && !!sessionId
        const actions = new Map<string, boolean>(
          (Array.isArray(catalog)
            ? catalog
            : (catalog.actions ?? catalog.tools ?? [])
          )
            .map(
              (item: {
                name?: string
                id?: string
                action?: string
                mutation?: boolean
              }) => [
                item.name ?? item.id ?? item.action,
                item.mutation !== false,
              ],
            )
            .filter(([name]: [string | undefined, boolean]) => !!name),
        )
        uiContract =
          binding.app?.descriptor?.uiContract ?? app.descriptor?.uiContract
        uiTools = Object.keys(uiContract?.chatEvents ?? {}).map(
          (type) => `ui.${app.id}.${type}`,
        )
        const view = await hostFetch(viewPath, { tools: uiTools })
        lease = view.lease
        if (disposed) return
        void (async () => {
          while (!disposed) {
            try {
              const response = await readResponse(
                `${viewPath}/poll?lease=${encodeURIComponent(lease)}`,
                { credentials: 'include', signal: controller.signal },
                40_000,
              )
              if (response.status === 410) {
                // The API restarted or the lease expired while this tab slept.
                // Register this same view again; never replay browser tool calls.
                uiInvocations.clear()
                const renewed = await hostFetch(viewPath, { tools: uiTools })
                lease = renewed.lease
                if (!disposed) setConnectionError('')
                void refreshState()
                continue
              }
              if ([401, 403, 404].includes(response.status)) {
                await revokeAccess()
                return
              }
              if (!response.ok)
                throw new Error(
                  'The workspace connection expired. Reload to reconnect.',
                )
              const events = await response.json()
              if (!disposed) setConnectionError('')
              if (events?.cancel) {
                uiInvocations.delete(events.cancel)
                await bridge?.sendToolResult({
                  content: [],
                  structuredContent: { canopyUiCancel: events.cancel },
                })
                continue
              }
              if (events?.tool && events?.invocationId) {
                const type = String(events.tool).slice(`ui.${app.id}.`.length)
                if (
                  !initialized ||
                  !uiTools.includes(events.tool) ||
                  !uiContract?.chatEvents[type]
                ) {
                  await hostFetch(`${viewPath}/result`, {
                    lease,
                    invocationId: events.invocationId,
                    result: {
                      isError: true,
                      content: [
                        {
                          type: 'text',
                          text: 'The typed UI handler is unavailable.',
                        },
                      ],
                    },
                  })
                  continue
                }
                uiInvocations.set(events.invocationId, lease)
                await bridge?.sendToolResult({
                  content: [],
                  structuredContent: {
                    canopyUiEvent: {
                      type,
                      payload: events.input?.payload,
                      invocationId: events.invocationId,
                    },
                  },
                })
                continue
              }
              const notifications = Array.isArray(events)
                ? events
                : Array.isArray(events?.events)
                  ? events.events
                  : events?.type
                    ? [events]
                    : []
              for (const notification of notifications) {
                if (
                  app.id !== 'onepager' ||
                  notification?.type !== 'onepager_result' ||
                  !notification.data ||
                  typeof notification.data !== 'object' ||
                  Array.isArray(notification.data)
                )
                  continue
                if (initialized)
                  await bridge?.sendToolResult({
                    content: [],
                    structuredContent: { data: notification.data },
                  })
                else pendingOnePagerResults.push(notification.data)
              }
              if (notifications.length > 0) void refreshState()
            } catch {
              if (disposed) break
              setConnectionError('Reconnecting to workspace updates…')
              try {
                await readDelay(3000, controller.signal)
              } catch {
                break
              }
            }
          }
        })()
        let context: Record<string, unknown> = {
          appInstanceId: binding.instance,
          bindingId,
          currentRevision: 0,
          selectedRevision: null,
          excerpt: '',
        }
        const hostContext = () => ({
          appId: app.id,
          theme:
            document.documentElement.dataset.theme === 'light'
              ? ('light' as const)
              : ('dark' as const),
          ...(app.id === 'onepager' && startupShareSlug
            ? { startupShareSlug }
            : {}),
        })
        bridge = new AppBridge(
          null,
          { name: 'Canopy', version: '0.2.0' },
          {
            serverTools: {},
            serverResources: {},
            ...(['chronicle', 'onepager'].includes(app.id)
              ? { downloadFile: {} }
              : {}),
            ...([
              'user-management',
              'agent-builder',
              'agent-simulator',
              'agent-tester',
              'loop-engineering',
            ].includes(app.id)
              ? { openLinks: {} }
              : {}),
            updateModelContext: { text: {} },
            ...(canChat ? { message: { text: {} } } : {}),
          },
          { hostContext: hostContext() },
        )
        {
          const updateTheme = () =>
            initialized && bridge?.setHostContext(hostContext())
          themeObserver = new MutationObserver(updateTheme)
          themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
          })
        }
        bridge.onsandboxready = () =>
          bridge!.sendSandboxResourceReady({
            html,
            sandbox: 'allow-scripts',
            csp: {},
          })
        bridge.oninitialized = async () => {
          clearTimeout(timer)
          initialized = true
          if (!disposed) setReady(true)
          const cached = recalledWorkspace(cacheKey)
          if (cached !== undefined && !disposed) {
            try {
              const cachedWorkflow = (
                cached as { workflow?: { revision?: number } }
              ).workflow
              if (typeof cachedWorkflow?.revision === 'number')
                builderRevision = cachedWorkflow.revision
              await bridge?.sendToolResult({
                content: [],
                structuredContent: { data: cached },
              })
              if (!disposed) setHasState(true)
            } catch {
              /* A fresh read remains authoritative. */
            }
          }
          if (disposed) return
          if (uiTools.length) {
            await hostFetch(
              `${viewPath}/context`,
              { lease, context: { ...context, viewId, lease } },
              'PUT',
            )
            publishComposerContext({ ...context, viewId, lease })
          }
          for (const result of pendingOnePagerResults)
            await bridge?.sendToolResult({
              content: [],
              structuredContent: { data: result },
            })
          pendingOnePagerResults.length = 0
          void refreshState()
          void sendInspection()
          refreshTimer = setInterval(() => void refreshState(), 3000)
        }
        bridge.oncalltool = async (params) => {
          if (disposed)
            throw new Error('This application view is no longer active.')
          if (params.name === 'canopy.ui_event_result') {
            const args = params.arguments ?? {}
            const invocationId = String(args.invocationId ?? '')
            const originalLease = uiInvocations.get(invocationId)
            if (!originalLease || originalLease !== lease)
              throw new Error('This UI invocation is no longer active.')
            uiInvocations.delete(invocationId)
            await hostFetch(`${viewPath}/result`, {
              lease: originalLease,
              invocationId,
              result: args.result,
            })
            return { content: [] }
          }
          if (params.name === 'canopy.ui_refresh' && uiContract) {
            await refreshState()
            return { content: [] }
          }
          if (!actions.has(params.name))
            throw new Error('This app does not expose that action.')
          const id = crypto.randomUUID()
          const response = await fetch(actionPath(bindingId, params.name), {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invocationId: id,
              ...(actions.get(params.name) ? { idempotencyKey: id } : {}),
              ...params.arguments,
            }),
          })
          if ([401, 403].includes(response.status)) {
            await revokeAccess()
            throw new Error('Your access to this application has changed.')
          }
          const result = await response.json()
          if (disposed)
            throw new Error('This application view is no longer active.')
          if (!response.ok)
            throw new Error(
              typeof result.detail === 'string'
                ? result.detail
                : JSON.stringify(result.detail ?? result),
            )
          if (
            app.id === 'loop-engineering' &&
            params.name === 'factory.discuss' &&
            sessionId &&
            boundSessionId === sessionId &&
            result.draft &&
            typeof result.draft.task_id === 'string' &&
            /^[a-f0-9]{32}$/.test(result.draft.task_id) &&
            typeof result.draft.title === 'string'
          ) {
            getSessionStore(sessionId).selectFactoryTask({
              id: result.draft.task_id,
              title: result.draft.title.slice(0, 4000),
            })
            window.dispatchEvent(
              new CustomEvent('bridge:factory-draft', {
                detail: { sessionId },
              }),
            )
          }
          void refreshState()
          if (app.id === 'agent-builder' && result?.data?.workflow) {
            builderRevision = Number(result.data.workflow.revision)
            publishBuilderWorkflow(bindingId, result.data.workflow)
          }
          return {
            content: [{ type: 'text', text: JSON.stringify(result) }],
            structuredContent: result,
          }
        }
        bridge.onreadresource = (params) =>
          hostFetch(`${root}/resource?uri=${encodeURIComponent(params.uri)}`)
        bridge.onupdatemodelcontext = async (params) => {
          const nextContext = {
            ...context,
            ...params.structuredContent,
            appInstanceId: binding.instance,
            bindingId,
          }
          context = nextContext
          const saveContext = () => {
            const update = contextUpdate.then(async () => {
              if (disposed) throw new Error('The application view closed.')
              await hostFetch(
                `${viewPath}/context`,
                { lease, context: { ...nextContext, viewId, lease } },
                'PUT',
              )
            })
            contextUpdate = update.catch(() => undefined)
            publishComposerContext(nextContext, update, saveContext)
            return update
          }
          await saveContext()
          return {}
        }
        bridge.onmessage = async (params) => {
          if (!canChat) throw new Error('This app has no conversation.')
          const text = params.content
            .filter((part) => part.type === 'text')
            .map((part) => part.text)
            .join('\n')
          const metadata = params.content.find((part) => part.type === 'text')
            ?._meta?.['dev.agentbridge/interaction']
          const interaction =
            metadata && typeof metadata === 'object'
              ? (metadata as Record<string, unknown>)
              : {}
          const uiEvent = params.content.find((part) => part.type === 'text')
            ?._meta?.['dev.canopy/ui-event']
          await hostFetch(`${root}/discuss`, {
            ...(uiEvent ? { ui_event: uiEvent } : {}),
            text,
            context,
            requestId: crypto.randomUUID(),
            ...(typeof interaction.displayText === 'string'
              ? { display_text: interaction.displayText }
              : {}),
            ...(typeof interaction.interactionId === 'string'
              ? { interaction_id: interaction.interactionId }
              : {}),
          })
          if (interaction.focusComposer === true)
            window.dispatchEvent(
              new CustomEvent('bridge:focus-composer', {
                detail: { sessionId: binding.session },
              }),
            )
          return {}
        }
        bridge.ondownloadfile = async ({ contents }) => {
          if (
            !['chronicle', 'onepager'].includes(app.id) ||
            contents.length !== 1
          )
            throw new Error('Unsupported app export')
          const item = contents[0]
          if (!item) throw new Error('Unsupported app export')
          if (app.id === 'onepager') {
            if (
              item.type !== 'resource' ||
              item.resource.mimeType !== 'text/csv' ||
              !('blob' in item.resource) ||
              item.resource.blob.length > 32_000_000
            )
              throw new Error('Expected a bounded CSV export')
            const name = item.resource.uri.split('/').pop() ?? ''
            if (!/^[a-z0-9-]{1,100}\.csv$/.test(name))
              throw new Error('Invalid export filename')
            const bytes = Uint8Array.from(atob(item.resource.blob), (char) =>
              char.charCodeAt(0),
            )
            const url = URL.createObjectURL(
              new Blob([bytes], { type: 'text/csv;charset=utf-8' }),
            )
            const link = document.createElement('a')
            link.href = url
            link.download = name
            link.click()
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
            return {}
          }
          if (
            item.type !== 'resource' ||
            item.resource.mimeType !== 'application/pdf' ||
            !('blob' in item.resource) ||
            item.resource.blob.length > 14_000_000
          )
            throw new Error('Expected a bounded PDF export')
          const name = item.resource.uri.split('/').pop() ?? ''
          if (!/^[a-z0-9-]{1,100}\.pdf$/.test(name))
            throw new Error('Invalid export filename')
          const bytes = Uint8Array.from(atob(item.resource.blob), (char) =>
            char.charCodeAt(0),
          )
          if (new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-')
            throw new Error('Invalid PDF export')
          const url = URL.createObjectURL(
            new Blob([bytes], { type: 'application/pdf' }),
          )
          const link = document.createElement('a')
          link.href = url
          link.download = name
          link.click()
          setTimeout(() => URL.revokeObjectURL(url), 60_000)
          return {}
        }
        bridge.onopenlink = async (params) => {
          const target = new URL(params.url)
          if (
            app.id === 'loop-engineering' &&
            target.protocol === 'https:' &&
            !target.username &&
            !target.password
          ) {
            window.open(target.href, '_blank', 'noopener,noreferrer')
            return {}
          }
          if (
            ['agent-builder', 'agent-simulator', 'agent-tester'].includes(
              app.id,
            ) &&
            target.origin === 'https://bridge.invalid' &&
            /^\/apps\/agent-simulator\/sessions\/[A-Za-z0-9_-]{1,160}$/.test(
              target.pathname,
            ) &&
            !target.search &&
            !target.hash
          ) {
            window.location.assign(target.pathname)
            return {}
          }
          const user = target.searchParams.get('user')
          if (
            app.id !== 'user-management' ||
            target.origin !== 'https://bridge.invalid' ||
            target.pathname !== '/connections' ||
            !user ||
            !/^[A-Za-z0-9_-]{1,160}$/.test(user)
          )
            throw new Error('Unknown directory destination')
          window.location.assign(`/?connections=${encodeURIComponent(user)}`)
          return {}
        }
        bridge.onrequestdisplaymode = async () => ({ mode: 'fullscreen' })
        await bridge.connect(
          new PostMessageTransport(frame.contentWindow!, frame.contentWindow!),
        )
        if (disposed) return
        frame.src = `${sandbox}/sandbox.html?parent=${encodeURIComponent(location.origin)}`
        timer = setTimeout(() => {
          if (!disposed)
            setError(
              'The app workspace did not finish opening. Reload to try again.',
            )
        }, 10000)
      } catch (failure) {
        if (!disposed) setError((failure as Error).message)
      } finally {
        if (disposed && lease)
          void fetch(`${viewPath}?lease=${encodeURIComponent(lease)}`, {
            method: 'DELETE',
          })
      }
    })()
    return () => {
      disposed = true
      themeObserver?.disconnect()
      refreshRef.current = () => {}
      controller.abort()
      clearTimeout(timer)
      clearInterval(refreshTimer)
      void bridge?.close()
      publishComposerContext(null)
      window.removeEventListener('bridge:analysis-inspect', inspect)
      window.removeEventListener(BUILDER_WORKFLOW_EVENT, receiveBuilderWorkflow)
      if (lease)
        void fetch(`${viewPath}?lease=${encodeURIComponent(lease)}`, {
          method: 'DELETE',
          keepalive: true,
        })
    }
  }, [
    attempt,
    app.id,
    app.digest,
    app.version,
    app.sandboxUrl,
    bindingId,
    sessionId,
    startupShareSlug,
    entrypoint,
    configuration.ui.mode,
    configuration.backend.mode,
  ])
  useEffect(() => {
    if (!backendStarting) refreshRef.current()
  }, [backendStarting])
  return (
    <div className="static-app-pane">
      {(!ready || backendStarting || !hasState) &&
        !backendPaused &&
        !error &&
        !stateError &&
        (hasState ? (
          <OpeningState compact title="Updating workspace…" />
        ) : (
          <SessionWorkspaceSkeleton title={app.title} />
        ))}
      {error && (
        <p role="alert" className="app-space-error">
          {error}{' '}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </button>
        </p>
      )}
      {(stateError || connectionError) && !backendStarting && (
        <div role="status" className="loading-state loading-state-compact">
          {hasState
            ? `Showing saved content. ${stateError || connectionError}`
            : stateError || connectionError}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => refreshRef.current()}
          >
            Retry now
          </button>
        </div>
      )}
      {backendPaused && !backendStarting && (
        <p role="status" className="app-space-empty">
          {app.title} is paused.{' '}
          {hasState
            ? 'Showing the last loaded workspace. Resume the session to refresh it.'
            : 'Resume the session to open its saved workspace.'}
        </p>
      )}
      <iframe
        ref={iframe}
        hidden={!hasState || !!error}
        inert={backendStarting || backendPaused}
        title={`${app.title} workspace`}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  )
}
